import { actionCreator, asyncActionsCreator } from '../common/utils';
import {
  AddOrUpdateTaskRequest,
  AddPageRangesForSelection,
  ChangeCategoryOrReadingRequest,
  ChangeReadingResponseItem,
  CreateTasksRequest,
  CreateTasksResponse,
  DeleteSubtaskRequest,
  GetManageTask,
  ManageTask,
  MoveToTodoRequest,
  OpenManageTaskDialog,
  PageRange,
  PileTask,
  PushDueDateForSelection,
  ReadingSourceInfoDto,
  SavePageRangesRequest,
  SetStartAheadForSelection,
  SetTimeNeededRequest,
  Subtask,
  Task,
  TaskCategoryDto,
  TaskDto,
  TaskStatusRequest,
  TaskStatusResponse,
  TimeEntry,
  UpdateTaskDetailsRequest,
  ViewPlannedTask
} from 'shovel-lib/types';
import {
  CreateRepeatPatternDialogType,
  SetDueDateForSelection,
  SetDueTimeForSelection,
  EstimateTasksRequest,
  PaceSuggestion,
  TasksCategorizationRequest,
  TasksEstimationByCategoryRequest
} from './types';
import { WorkloadTask } from 'shovel-lib/types/task';
import { CategorySuggestion } from '@utils/categoryTime';

const asyncActions = asyncActionsCreator('@@task');
const action = actionCreator('@@task');

export const openManageTaskDialog = action<OpenManageTaskDialog | PileTask | WorkloadTask>('OPEN_MANAGE_TASK_DIALOG');
export const closeManageTaskDialog = action<void>('CLOSE_MANAGE_TASK_DIALOG');

export const getManageTask = asyncActions<GetManageTask, ManageTask, string>('GET_MANAGE_TASK');
export const saveManageTask = asyncActions<
  { task: WorkloadTask | ManageTask; request: UpdateTaskDetailsRequest },
  { taskId: number } & {
    request: UpdateTaskDetailsRequest;
    response: UpdateTaskDetailsRequest & { timeEntries: TimeEntry[] };
    task: any;
  },
  string
>('SAVE_MANAGE_TASK');
export const getPageRanges = asyncActions<number, { taskId: number; pageRanges: PageRange[] }, string>('GET_PAGE_RANGES');
export const savePageRanges = asyncActions<SavePageRangesRequest, { taskId: number; data: Task }, string>(
  'SAVE_PAGE_RANGES'
);

// tempId: a new subtask added from the Task List's lines, shown under it at once by this (negative) id until the server
// gives it its own.
export const addOrUpdateSubtask = asyncActions<
  AddOrUpdateTaskRequest & { tempId?: number },
  AddOrUpdateTaskRequest & { tempId?: number },
  string
>('ADD_OR_UPDATE_SUBTASK');
// A save from the Task List that failed: the fields it showed at once go back to what they were.
export const restoreTaskFields = action<{ taskId: number; fields: Record<string, any> }>('RESTORE_TASK_FIELDS');
export const deleteSubtask = asyncActions<DeleteSubtaskRequest, DeleteSubtaskRequest, string>('DELETE_SUBTASK');
export const updateTaskStatus = asyncActions<
  {
    taskId: number;
    data: TaskStatusRequest & { plannedTaskId?: number };
    callback?: any;
    onError?: () => void;
    // When the caller's animation, started with the save, ends (ms since 1970): the update is held until then, so
    // the task doesn't jump away mid-animation. None for callers that animate first and save after.
    animationEndsAt?: number;
    // A reading completed from a session not yet used: the pace that session read its pages at, for the book's
    // "Time per page" question once the completion is saved.
    readingPace?: { reading: ReadingSourceInfoDto; newTimePerPage: number; courseId: number };
    // The session whose popup this came from, which asks a question the save brings if it is still waiting for it.
    askIn?: number;
  },
  TaskStatusRequest & TaskStatusResponse & { taskId: number; plannedTaskId?: number },
  string
>('UPDATE_TASK_STATUS');
export const moveToTodo = asyncActions<
  { taskId: number; data: MoveToTodoRequest },
  MoveToTodoRequest & TaskStatusResponse & { taskId: number },
  string
>('MOVE_TO_TODO');
export const deleteTask = asyncActions<
  { id: number; deleteForever?: boolean },
  { id: number; deleteForever?: boolean },
  string
>('DELETE_TASK');

type WithCategoryAndSource<T> = T & {
  readingSourceId?: number;
  taskCategoryId?: number;
  workloadTable?: string;
};
export const createTasks = asyncActions<
  WithCategoryAndSource<CreateTasksRequest> & {
    isFromSidebar?: boolean;
    planTaskEvent?: { start: any; end: any } & any;
    callback?: any;
    /** Duplicate: with "Sort: By hand", the copy goes right under this task (its original). */
    placeAfterTaskId?: number;
  },
  WithCategoryAndSource<CreateTasksResponse> & {
    isFromSidebar?: boolean;
    /** Hand-order numbers to show at once, by task: the copy's place under its original (placeAfterTaskId). */
    listOrders?: Record<number, number>;
    /** Made by Duplicate from this task: shown under it, not held at its group's end with other new tasks. */
    copyOf?: number;
  },
  string
>('CREATE_TASKS');

export const openTasksModal = action<TaskDto[]>('OPEN_TASKS_MODAL');
export const closeTasksModal = action<void>('CLOSE_TASKS_MODAL');

export const deleteTasks = asyncActions<
  { ids: number[]; deleteForever?: boolean },
  { ids: number[]; deleteForever?: boolean },
  string
>('DELETE_TASKS');
export const completeTasks = asyncActions<number[], number[], string>('COMPLETE_TASKS');
export const moveTasksToTodo = asyncActions<number[], number[], string>('MOVE_TASKS_TO_TODO');
export const setTasksStartAhead = asyncActions<SetStartAheadForSelection, SetStartAheadForSelection, string>(
  'SET_TASKS_START_AHEAD'
);
export const setTasksPriority = asyncActions<
  { taskIds: number[]; priority: number },
  { taskIds: number[]; priority: number },
  string
>('SET_TASKS_PRIORITY');
export const setTasksDueDate = asyncActions<SetDueDateForSelection, Task[], string>('SET_TASKS_DUE_DATE');
export const setTasksDueTime = asyncActions<SetDueTimeForSelection, Task[], string>('SET_TASKS_DUE_TIME');
export const pushTasksDueDate = asyncActions<PushDueDateForSelection, Task[], string>('PUSH_TASKS_DUE_DATE');
// Start ahead follows the time on the server; each task's comes back as `startAhead` (by task id).
export const setTimeNeeded = asyncActions<
  SetTimeNeededRequest,
  SetTimeNeededRequest & { startAhead?: Record<number, number> },
  string
>('ADD_MORE_TIME');
export const addPageRangeForTasks = asyncActions<AddPageRangesForSelection, Task[], string>('ADD_PAGE_RANGE_FOR_TASKS');
export const tasksCategorization = asyncActions<
  TasksCategorizationRequest & { onFinish?: (ok?: boolean) => void; silent?: boolean; successMessage?: string },
  { tasks: Task[]; taskCategories: TaskCategoryDto[]; courseId: number },
  string
>('TASKS_CATEGORIZATION');
export const tasksEstimationByCategory = asyncActions<TasksEstimationByCategoryRequest, Task[], string>(
  'TASKS_ESTIMATION_BY_CATEGORY'
);
export const estimateTasks = asyncActions<EstimateTasksRequest, { courseId: number; tasks: WorkloadTask[] }, string>(
  'ESTIMATE_TASKS'
);

export const openNewCreateTaskDialog = action<
  { courseId: number; taskCategoryId?: number; readingSourceId?: number } | undefined | void
>('OPEN_NEW_CREATE_TASK_DIALOG');
export const closeNewCreateTaskDialog = action<void>('CLOSE_NEW_CREATE_TASK_DIALOG');

export const openRepeatPatternDialog = action<CreateRepeatPatternDialogType>('OPEN_REPEAT_PATTERN_DIALOG');
export const closeRepeatPatternDialog = action<void>('CLOSE_REPEAT_PATTERN_DIALOG');

export const changeTaskCategory = asyncActions<
  ChangeCategoryOrReadingRequest,
  { taskIds: number[]; category: TaskCategoryDto },
  string
>('CHANGE_TASK_CATEGORY');

export const changeReadingSource = asyncActions<ChangeCategoryOrReadingRequest, ChangeReadingResponseItem[], string>(
  'CHANGE_READING_SOURCE'
);

export const changeCourse = asyncActions<ChangeCategoryOrReadingRequest & { courseName?: string }, Task[], string>(
  'CHANGE_COURSE'
);

export const reorderSubtasks = asyncActions<
  { id: number; subtasks: Subtask[] },
  { id: number; subtasks: Subtask[] },
  string
>('REORDER_SUBTASKS');

export const restoreTasks = asyncActions<number[], number[], string>('RESTORE_TASKS');

export const addPlannedTask = asyncActions<
  { taskId: number; courseName?: string; textColor?: string; title: string },
  ViewPlannedTask & { taskId: number; courseName?: string; textColor?: string; title: string },
  string
>('ADD_PLANNED_TASK');

export const openNewEstimateDialog = action<PaceSuggestion>('OPEN_NEW_EST_DIALOG');

export const closeNewEstimateDialog = action<void>('CLOSE_NEW_EST_DIALOG');

// Asked once a completed task took 20% or more longer or shorter than its category's time.
export const openCategoryTimeDialog = action<CategorySuggestion>('OPEN_CATEGORY_TIME_DIALOG');

export const closeCategoryTimeDialog = action<void>('CLOSE_CATEGORY_TIME_DIALOG');

// export const getCourses = createAsyncAction(
//   TaskGetCoursesActionTypes.TASK_GET_COURSES,
//   TaskGetCoursesActionTypes.TASK_GET_COURSES_SUCCESS,
//   TaskGetCoursesActionTypes.TASK_GET_COURSES_FAILURE
// )<void, NameDto[], string>();
//
// export const getGradeUnits = createAsyncAction(
//   TaskGetGradeUnitsActionTypes.TASK_GET_GRADE_UNITS,
//   TaskGetGradeUnitsActionTypes.TASK_GET_GRADE_UNITS_SUCCESS,
//   TaskGetGradeUnitsActionTypes.TASK_GET_GRADE_UNITS_FAILURE
// )<GetGradeUnitsRequest, NameDto[], string>();
//
// export const getReadingSources = createAsyncAction(
//   TaskGetReadingSourcesActionTypes.TASK_GET_READING_SOURCES,
//   TaskGetReadingSourcesActionTypes.TASK_GET_READING_SOURCES_SUCCESS,
//   TaskGetReadingSourcesActionTypes.TASK_GET_READING_SOURCES_FAILURE
// )<GetReadingSourcesRequest, ReadingSourceInfoDto[], string>();
//
// export const getClassAppearancesOnDate = createAsyncAction(
//   GetClassAppearancesOnDateActionTypes.GET_CLASS_APPEARANCES_ON_DATE,
//   GetClassAppearancesOnDateActionTypes.GET_CLASS_APPEARANCES_ON_DATE_SUCCESS,
//   GetClassAppearancesOnDateActionTypes.GET_CLASS_APPEARANCES_ON_DATE_FAILURE
// )<ClassAppearancesRequest, ClassAppearance[], string>();
